import { getAllSolutionScenarios } from '@/data/solutions/registry';
import { whoWeServeItems } from '@/data/marketing/whoWeServe';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { cn } from '@/lib/utils';
import { getIndustryHref } from '@/config/site';

// Map solution IDs to custom brand icons
const solutionIcons: Record<string, string> = {
  'automotive': '/icons/solutions/icon-automotive.png',
  'marine': '/icons/solutions/icon-marine.png',
  'appliances': '/icons/solutions/icon-appliances.png',
  'office-equipment': '/icons/solutions/icon-office-equipment.png',
  'equipment': '/icons/solutions/icon-industrial-equipment.png',
  'home-builders': '/icons/solutions/icon-home-builders.png',
  'travel-hospitality': '/icons/solutions/icon-travel-hospitality.png',
  'manufacturing': '/icons/solutions/icon-manufacturing.png',
  'mining': '/icons/solutions/icon-mining.png',
  'industrial': '/icons/solutions/icon-industrial.png',
  'trades': '/icons/solutions/icon-trades.png',
  'plumbing': '/icons/solutions/icon-plumbing.png',
  'repair-services': '/icons/solutions/icon-repair-services.png',
  'real-estate': '/icons/solutions/icon-real-estate.png',
  'clubs-associations': '/icons/solutions/icon-clubs-associations.png',
  'events': '/icons/solutions/icon-events.png',
};

// Map who-we-serve IDs to custom brand icons
const whoWeServeIcons: Record<string, string> = {
  business: '/icons/who-we-serve/icon-who-business.png',
  developers: '/icons/who-we-serve/icon-who-developers.png',
  partners: '/icons/who-we-serve/icon-who-partners.png',
  consumers: '/icons/who-we-serve/icon-who-consumers.png',
};

const SolutionsDropdown = () => {
  const scenarios = getAllSolutionScenarios();

  return (
    <NavigationMenu>
      <NavigationMenuList>
        <NavigationMenuItem>
          <NavigationMenuTrigger className="bg-transparent text-white text-base font-medium hover:bg-white/10 hover:text-white focus:bg-white/10 data-[state=open]:bg-white/10 px-2 py-2 h-auto">
            Solutions
          </NavigationMenuTrigger>
          <NavigationMenuContent>
            <div className="w-[min(920px,calc(100vw-2rem))] bg-white/95 backdrop-blur-xl border border-gray-200 rounded-xl p-4 md:p-6 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-4 md:gap-6">
                {/* Who We Serve Section - Left Column */}
                <div className="md:w-[260px] lg:w-[300px] shrink-0 md:border-r border-b md:border-b-0 border-gray-200 pb-4 md:pb-0 md:pr-6">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Who We Serve
                  </h3>
                  <div className="space-y-2">
                    {whoWeServeItems.map((segment) => {
                      const iconSrc = whoWeServeIcons[segment.id];
                      return (
                        <NavigationMenuLink key={segment.id} asChild>
                          <a
                            href={segment.href}
                            className={cn(
                              "group flex flex-col gap-2 p-3 rounded-xl transition-all duration-200",
                              "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                            )}
                          >
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg">
                                <img src={iconSrc} alt={segment.name} className="w-6 h-6 object-contain" />
                              </div>
                            </div>
                            <div className="space-y-0.5">
                              <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                                {segment.name}
                              </h4>
                              <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                                {segment.tagline}
                              </p>
                            </div>
                          </a>
                        </NavigationMenuLink>
                      );
                    })}
                  </div>
                </div>

                {/* Solutions Section - Right Column */}
                <div className="flex-1">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                    Solutions by Industry
                  </h3>
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {scenarios.map((scenario) => {
                      const iconSrc = solutionIcons[scenario.meta.id];
                      return (
                        <NavigationMenuLink key={scenario.meta.id} asChild>
                          <a
                            href={getIndustryHref(scenario.meta.slug)}
                            className={cn(
                              "group flex flex-col gap-2 p-3 rounded-xl transition-all duration-200",
                              "hover:bg-purple-50 focus:bg-purple-50 focus:outline-none"
                            )}
                          >
                            <div className="p-1.5 bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg w-fit">
                              <img src={iconSrc} alt={scenario.meta.name} className="w-6 h-6 object-contain" />
                            </div>
                            <div className="space-y-0.5">
                              <h4 className="text-gray-900 font-semibold text-sm group-hover:text-purple-600 transition-colors">
                                {scenario.meta.name}
                              </h4>
                              <p className="text-gray-500 text-xs line-clamp-2 leading-relaxed">
                                {scenario.meta.tagline}
                              </p>
                            </div>
                          </a>
                        </NavigationMenuLink>
                      );
                    })}
                  </div>
                </div>
              </div>

            </div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default SolutionsDropdown;
